/**
 * @fileoverview Prior court application fields
 * @module routes/dashboard/-ui.job-relation-back-fields
 *
 * Used by:
 *  - Job Settings → Appointment Details (with the override input)
 *  - Create Job dialog, to capture the answer at intake (without the override)
 *
 * Purpose:
 *  - Ask the one question that decides a voluntary administration's
 *    relation-back day (MOR-901): was a winding-up application already filed
 *    when the administrators were appointed, and is it still on foot?
 *  - Show the practitioner the resulting date, and let them override it.
 *
 * Why this is a voluntary-administration question: an administration is the
 * only appointment that can be preceded by a live winding-up application. If
 * one was filed BEFORE the administration began and never dismissed or
 * withdrawn, its FILING date is the relation-back day (ILRA s 91) — not the
 * date of appointment, which is what Morgan assumed before.
 *
 * The section stays visible after the VA converts to a liquidation, but only
 * when an answer was already recorded: `deriveRelationBack` keeps reading it,
 * so hiding it would leave a date driving the review windows with no way to
 * see or correct the fact behind it.
 *
 * Presentational: owns no state and no mutation, so it can sit inside a
 * settings section's Save flow or the create dialog's submit without
 * knowing which — the same way `DayOneFieldsSection` does.
 */

import type { Doc } from "@cvx/_generated/dataModel";
import type { WindingUpApplicationStatus } from "@cvx/jobs/schema";
import { deriveRelationBack } from "@cvx/keyDates/derivations";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { format, isValid, parseISO } from "date-fns";
import { Scale } from "lucide-react";

/**
 * Types that keep showing the section once an answer exists, because
 * `deriveRelationBack` keeps honouring it after a VA converts. A court
 * liquidation is deliberately absent: its own winding-up application already
 * has an input in the appointment details grid, and its relation-back day
 * already defaults to that filing date.
 */
const CONVERTED_TYPES = new Set([
  "cvl",
  "simplified_liquidation",
  "deed_of_company_arrangement",
]);

const STATUS_OPTIONS: Array<{
  value: Exclude<WindingUpApplicationStatus, "none">;
  label: string;
}> = [
  { value: "on_foot", label: "Currently proceeding" },
  { value: "dismissed", label: "Dismissed" },
  { value: "withdrawn", label: "Withdrawn" },
];

export interface RelationBackFieldsProps {
  /** Current (possibly unsaved) appointment type from the parent form. */
  appointmentType: string;
  /** Job dates the preview needs. `administrationStartDate` is set only once
   *  the VA has converted; before that the appointment date is the VA date. */
  context: {
    appointmentDate: string;
    administrationStartDate: string;
  };
  /** ISO date the winding-up application was filed. */
  applicationDate: string;
  onApplicationDateChange: (next: string) => void;
  /** Unset means the question hasn't been answered yet. */
  status: WindingUpApplicationStatus | undefined;
  onStatusChange: (next: WindingUpApplicationStatus | undefined) => void;
  /**
   * Manual relation-back date override. Omit `onOverrideChange` to hide the
   * override input — job intake captures the facts, and correcting the date the
   * law produces from them belongs on the job, not in the create dialog.
   */
  override?: string;
  onOverrideChange?: (next: string) => void;
  /**
   * Show the explanatory copy — the heading with the legislative reasoning, and
   * the note on what a dismissed application means. Off in the create dialog,
   * where the questions are self-explanatory and the surrounding form is already
   * long; the reasoning stays available on the job.
   */
  showGuidance?: boolean;
  disabled: boolean;
}

/**
 * Prior court application section.
 *
 * @component
 * @example
 * <RelationBackFieldsSection
 *   appointmentType="voluntary_administration"
 *   context={{ appointmentDate, administrationStartDate }}
 *   applicationDate={applicationDate}
 *   onApplicationDateChange={setApplicationDate}
 *   status={status}
 *   onStatusChange={setStatus}
 *   override={override}
 *   onOverrideChange={setOverride}
 *   disabled={false}
 * />
 */
export function RelationBackFieldsSection({
  appointmentType,
  context,
  applicationDate,
  onApplicationDateChange,
  status,
  onStatusChange,
  override = "",
  onOverrideChange,
  showGuidance = true,
  disabled,
}: RelationBackFieldsProps) {
  const isVa = appointmentType === "voluntary_administration";
  const hasRecordedAnswer = status !== undefined && status !== "none";
  if (!isVa && !(CONVERTED_TYPES.has(appointmentType) && hasRecordedAnswer)) {
    return null;
  }

  const answeredYes = status !== undefined && status !== "none";

  // Preview through the real resolver so the section can never disagree with
  // the backend about which rule applies.
  const preview = deriveRelationBack({
    appointmentType: appointmentType || undefined,
    appointmentDate: context.appointmentDate || undefined,
    administrationStartDate: context.administrationStartDate || undefined,
    windingUpApplicationDate: applicationDate || undefined,
    windingUpApplicationStatus: status,
    relationBackDateOverride: override || undefined,
  } as unknown as Doc<"jobs">);

  const handleAnswerChange = (value: string) => {
    if (value === "no") {
      onStatusChange("none");
      return;
    }
    // Default to the answer that changes the date, so the practitioner has to
    // actively downgrade it rather than accidentally leave it unrecorded.
    onStatusChange(status && status !== "none" ? status : "on_foot");
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border/50 bg-secondary/20 p-4">
      {showGuidance && (
        <div className="flex items-start gap-2">
          <Scale className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div>
            <h4 className="text-sm font-semibold">Prior Court Application</h4>
            <p className="text-xs text-muted-foreground">
              Where a winding-up application was filed before the administration
              began and has not been dismissed or withdrawn, the date it was
              FILED is the relation-back day (ILRA s&nbsp;91) — not the date of
              appointment. This answer keeps applying if the administration
              later converts to a liquidation.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>
            Was a court application to wind up the company filed before your
            appointment?
          </Label>
          <Select
            value={status === undefined ? "" : answeredYes ? "yes" : "no"}
            onValueChange={handleAnswerChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an answer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {answeredYes && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label>Date winding-up application was filed</Label>
              <DatePicker
                value={applicationDate}
                onChange={(next) => onApplicationDateChange(next ?? "")}
                placeholder="Select date"
                disabled={disabled}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Status of that application</Label>
              <Select
                value={status}
                onValueChange={(next) =>
                  onStatusChange(next as WindingUpApplicationStatus)
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showGuidance && (
                <p className="text-[11px] leading-snug text-muted-foreground">
                  A dismissed or withdrawn application can no longer set the
                  relation-back day, so it falls back to the date the
                  administrators were appointed.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Computed result — the whole point of the section. */}
      <div className="flex flex-col gap-1 rounded-md border border-border/50 bg-background/60 p-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-xs text-muted-foreground">
            Applied relation-back date
          </span>
          <span className="text-sm font-semibold tabular-nums">
            {preview.date
              ? formatDisplayDate(preview.date)
              : "Not yet available"}
          </span>
          {preview.section && (
            <span className="text-[11px] text-muted-foreground">
              {preview.section}
            </span>
          )}
          {preview.assumed && !isVa && (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              Assumed
            </span>
          )}
          {preview.isOverride && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
              Manual override
            </span>
          )}
        </div>
        {preview.reason && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            {formatDatesInCopy(preview.reason)}
          </p>
        )}
      </div>

      {onOverrideChange && (
        <div className="flex flex-col gap-1.5">
          <Label>Relation-Back Date Override</Label>
          <DatePicker
            value={override}
            onChange={(next) => onOverrideChange(next ?? "")}
            placeholder="Leave blank to use the applied date"
            disabled={disabled}
          />
          <p className="text-[11px] leading-snug text-muted-foreground">
            Overrides the applied date everywhere it is used — the calendar
            anchor, the investigation review windows and the AI summary agents.
            Clear it to return to the applied date.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * ISO `yyyy-MM-dd` → `dd/MM/yyyy` for display. Matches the DatePicker, so the
 * applied date and the date the practitioner just picked look the same.
 * Unparseable values are shown as stored rather than dropped.
 */
function formatDisplayDate(iso: string): string {
  const parsed = parseISO(iso);
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : iso;
}

/** Rewrites ISO `yyyy-MM-dd` tokens inside a reason string to `dd/MM/yyyy`. */
function formatDatesInCopy(copy: string): string {
  return copy.replace(/\b\d{4}-\d{2}-\d{2}\b/g, formatDisplayDate);
}
